import { useEffect, useState, useMemo } from 'react';
import { useGameSession } from '../../hooks/useGame';
import { useGameAction } from '../../hooks/useGame';
import { useCards } from '../../hooks/useCards';
import { GameState, FieldCard } from '../../types/game';
import { supabase } from '../../lib/supabaseClient';
import { Card } from '../inventory/Card';
import { CardDetailModal } from '../inventory/CardDetailModal';
import './GameBoard.css';

interface GameBoardProps {
  gameSessionId: string;
  onExit: () => void;
}

export function GameBoard({ gameSessionId, onExit }: GameBoardProps) {
  const { data: gameSession, isLoading } = useGameSession(gameSessionId);
  const gameAction = useGameAction();
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [selectedFieldCard, setSelectedFieldCard] = useState<{ cardId: number; isMyField: boolean } | null>(null);
  const [attackMode, setAttackMode] = useState<{ attackerCardId: number; attackerCard: any } | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null); // 남은 시간 (초)
  const [damageNumbers, setDamageNumbers] = useState<Array<{ id: number; value: number; x: number; y: number; isHeal?: boolean }>>([]);
  const [opponentProfile, setOpponentProfile] = useState<{ username: string | null; avatar: string | null } | null>(null);

  // Realtime 구독은 useGameSession 훅에서 처리됨
  // 여기서는 추가적인 UI 업데이트만 필요하면 추가

  // 타이머: 현재 턴 플레이어의 35초 카운트다운 (내 턴이든 상대 턴이든 모두 표시)
  useEffect(() => {
    if (!gameSession || gameSession.status !== 'playing') {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const lastActionAt = gameSession.last_action_at 
        ? new Date(gameSession.last_action_at) 
        : new Date(gameSession.created_at);
      const now = new Date();
      const elapsed = (now.getTime() - lastActionAt.getTime()) / 1000;
      const remaining = Math.max(0, 35 - elapsed);
      
      setTimeRemaining(remaining);

      // 35초 지나면 자동으로 시간 패배 처리 (Edge Function에서도 처리하지만 클라이언트에서도 확인)
      if (remaining <= 0) {
        // Edge Function에서 처리되므로 여기서는 UI만 업데이트
        // Realtime으로 게임 상태가 업데이트될 것임
      }
    };

    // 즉시 업데이트
    updateTimer();

    // 1초마다 업데이트
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [gameSession]);

  // 현재 사용자 ID 및 턴 확인
  useEffect(() => {
    if (!gameSession) return;

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setCurrentUserId(user.id);
        setIsMyTurn(gameSession.current_turn_user_id === user.id);
      }
    });
  }, [gameSession]);

  // 상대 프로필 정보 가져오기
  useEffect(() => {
    if (!gameSession || !currentUserId) {
      setOpponentProfile(null);
      return;
    }

    const opponentUserId = gameSession.player1_id === currentUserId
      ? gameSession.player2_id
      : gameSession.player1_id;

    if (!opponentUserId) {
      setOpponentProfile(null);
      return;
    }

    supabase
      .from('profiles')
      .select('github_username, avatar_emoji')
      .eq('id', opponentUserId)
      .single()
      .then(({ data, error }) => {
        if (error || !data) {
          setOpponentProfile({ username: null, avatar: null });
          return;
        }
        setOpponentProfile({
          username: data.github_username,
          avatar: data.avatar_emoji,
        });
      });
  }, [gameSession, currentUserId]);

  // 현재 플레이어와 상대 플레이어 구분
  const { myPlayer, opponentPlayer } = useMemo(() => {
    if (!gameSession || !currentUserId) {
      return { myPlayer: null, opponentPlayer: null };
    }

    const gameState = gameSession.game_state as GameState;
    const isP1 = gameSession.player1_id === currentUserId;
    
    return {
      myPlayer: isP1 ? gameState.player1 : gameState.player2,
      opponentPlayer: isP1 ? gameState.player2 : gameState.player1,
    };
  }, [gameSession, currentUserId]);

  // 핸드 카드 정보 가져오기
  const handCardIds = useMemo(() => {
    return myPlayer?.hand || [];
  }, [myPlayer]);

  const { data: handCards } = useCards(handCardIds, !!myPlayer);

  // 필드 카드 정보 가져오기
  const myFieldCardIds = useMemo(() => {
    return myPlayer?.field?.map((fc: FieldCard) => fc.card_id) || [];
  }, [myPlayer]);

  const opponentFieldCardIds = useMemo(() => {
    return opponentPlayer?.field?.map((fc: FieldCard) => fc.card_id) || [];
  }, [opponentPlayer]);

  const { data: myFieldCards } = useCards(myFieldCardIds, !!myPlayer);
  const { data: opponentFieldCards } = useCards(opponentFieldCardIds, !!opponentPlayer);

  const handleEndTurn = async () => {
    if (!gameSession || !isMyTurn) return;

    try {
      await gameAction.mutateAsync({
        gameSessionId: gameSession.id,
        action: {
          type: 'end_turn',
          player_id: gameSession.current_turn_user_id || '',
        },
      });
    } catch (error) {
      console.error('Failed to end turn:', error);
    }
  };

  const handleDrawCard = async () => {
    if (!gameSession || !isMyTurn) return;

    try {
      await gameAction.mutateAsync({
        gameSessionId: gameSession.id,
        action: {
          type: 'draw_card',
          player_id: gameSession.current_turn_user_id || '',
        },
      });
    } catch (error) {
      console.error('Failed to draw card:', error);
    }
  };

  const handlePlayCard = async (cardId: number, card: any) => {
    if (!gameSession || !isMyTurn || !myPlayer) return;

    // 마나 확인
    if (myPlayer.mana < (card.cost || 0)) {
      alert('마나가 부족합니다.');
      return;
    }

    // 크리처인 경우 필드 공간 확인
    if (card.card_type === 'creature') {
      const fieldSize = myPlayer.field?.length || 0;
      if (fieldSize >= 5) {
        alert('필드가 가득 찼습니다.');
        return;
      }
    }

    try {
      await gameAction.mutateAsync({
        gameSessionId: gameSession.id,
        action: {
          type: 'play_card',
          player_id: gameSession.current_turn_user_id || '',
          data: {
            card_id: cardId,
            cost: card.cost,
            card_type: card.card_type,
            hp: card.hp,
            attack: card.attack,
            defense: card.defense,
            position: myPlayer.field?.length || 0,
            skill_effect: card.skill_effect, // 스킬 효과 전달 (스펠용)
          },
        },
      });
      setSelectedCardId(null); // 카드 상세 모달 닫기
    } catch (error) {
      console.error('Failed to play card:', error);
      alert(error instanceof Error ? error.message : '카드 플레이에 실패했습니다.');
    }
  };

  const handleAttack = async (attackerCardId: number, attackerCard: any, targetCardId?: number) => {
    if (!gameSession || !isMyTurn || !myPlayer || !opponentPlayer) return;

    try {
      let targetData: any = {};
      let damage = 0;
      let targetX = 0;
      let targetY = 0;

      if (targetCardId) {
        // 필드 크리처 공격
        const targetFieldCard = opponentPlayer.field?.find(
          (fc: FieldCard) => fc.card_id === targetCardId
        );
        const targetCard = opponentFieldCards?.find((c) => c.id === targetCardId);
        
        if (!targetFieldCard || !targetCard) {
          alert('공격 대상을 찾을 수 없습니다.');
          return;
        }

        // 데미지 계산
        damage = Math.max(0, (attackerCard.attack || 0) - (targetCard.defense || 0));
        
        // 타겟 카드 위치 계산 (대략적인 위치)
        const targetIndex = opponentPlayer.field?.findIndex((fc: FieldCard) => fc.card_id === targetCardId) || 0;
        targetX = 50 + (targetIndex % 3) * 120; // 필드 카드 위치 추정
        targetY = 100; // 상대 필드 Y 위치

        targetData = {
          target_card_id: targetCardId,
          target_defense: targetCard.defense || 0,
        };
      } else {
        // 직접 공격
        damage = attackerCard.attack || 0;
        targetX = window.innerWidth / 2; // 화면 중앙
        targetY = 150; // 상대 플레이어 정보 위치

        targetData = {
          target_player_id: opponentPlayer.user_id,
        };
      }

      // 데미지 숫자 표시
      if (damage > 0) {
        const damageId = Date.now();
        setDamageNumbers(prev => [...prev, { id: damageId, value: damage, x: targetX, y: targetY }]);
        // 0.6초 후 제거
        setTimeout(() => {
          setDamageNumbers(prev => prev.filter(d => d.id !== damageId));
        }, 600);
      }

      await gameAction.mutateAsync({
        gameSessionId: gameSession.id,
        action: {
          type: 'attack',
          player_id: gameSession.current_turn_user_id || '',
          data: {
            attacker_card_id: attackerCardId,
            attacker_attack: attackerCard.attack || 0,
            ...targetData,
          },
        },
      });
    } catch (error) {
      console.error('Failed to attack:', error);
      alert(error instanceof Error ? error.message : '공격에 실패했습니다.');
    }
  };

  const handleUseSkill = async (cardId: number, card: any) => {
    if (!gameSession || !isMyTurn || !myPlayer) return;

    const skillEffect = card.skill_effect;
    if (!skillEffect) {
      alert('이 카드에는 스킬이 없습니다.');
      return;
    }

    // 스킬 마나 비용 확인
    const skillCost = (skillEffect as any)?.cost || 0;
    if (myPlayer.mana < skillCost) {
      alert('마나가 부족합니다.');
      return;
    }

    // 스킬 대상 선택 (필요한 경우)
    let targetCardId: number | undefined = undefined;
    let targetPlayerId: string | undefined = undefined;

    const effects = (skillEffect as any)?.effects || [];
    const needsTarget = effects.some((e: any) => e.target === 'target');

    if (needsTarget) {
      // 타겟이 필요한 스킬인 경우 (간단히 처리)
      // 실제로는 UI에서 타겟을 선택하도록 해야 함
      const targetId = prompt('대상 카드 ID를 입력하세요 (직접 공격은 Enter):');
      if (targetId === null) return; // 취소
      if (targetId === '') {
        targetPlayerId = opponentPlayer.user_id;
      } else {
        targetCardId = parseInt(targetId);
      }
    }

    try {
      await gameAction.mutateAsync({
        gameSessionId: gameSession.id,
        action: {
          type: 'use_skill',
          player_id: gameSession.current_turn_user_id || '',
          data: {
            card_id: cardId,
            skill_effect: skillEffect,
            target_card_id: targetCardId,
            target_player_id: targetPlayerId,
          },
        },
      });
      setSelectedFieldCard(null); // 카드 상세 모달 닫기
    } catch (error) {
      console.error('Failed to use skill:', error);
      alert(error instanceof Error ? error.message : '스킬 사용에 실패했습니다.');
    }
  };

  // 필드 카드 맵 생성 (빠른 조회용)
  const myFieldCardMap = useMemo(() => {
    if (!myPlayer) return new Map<number, FieldCard>();
    const map = new Map<number, FieldCard>();
    myPlayer.field?.forEach((fc: FieldCard) => {
      map.set(fc.card_id, fc);
    });
    return map;
  }, [myPlayer]);

  const opponentFieldCardMap = useMemo(() => {
    if (!opponentPlayer) return new Map<number, FieldCard>();
    const map = new Map<number, FieldCard>();
    opponentPlayer.field?.forEach((fc: FieldCard) => {
      map.set(fc.card_id, fc);
    });
    return map;
  }, [opponentPlayer]);

  if (isLoading || !gameSession) {
    return (
      <div className="game-board">
        <div className="game-board__loading">게임을 불러오는 중...</div>
      </div>
    );
  }

  if (!gameSession || !myPlayer || !opponentPlayer) {
    return (
      <div className="game-board">
        <div className="game-board__loading">게임을 불러오는 중...</div>
      </div>
    );
  }

  const gameState = gameSession.game_state as GameState;

  // 게임 종료 상태
  if (gameSession.status === 'finished') {
    const isWinner = gameSession.winner_id === currentUserId;
    const isTimeout = (gameSession as any).timeout === true; // Edge Function에서 반환한 timeout 플래그
    
    return (
      <div className="game-board__game-over">
        <h2 className="game-board__game-over-title">
          {isTimeout 
            ? (isWinner ? '⏱️ 상대방 시간 패배!' : '⏱️ 시간 패배!') 
            : (isWinner ? '🎉 승리!' : '😭 패배!')}
        </h2>
        <p className="game-board__game-over-message">
          {isTimeout
            ? (isWinner 
                ? '상대방이 시간 내에 액션을 취하지 않아 승리했습니다!' 
                : '시간 내에 액션을 취하지 않아 패배했습니다.')
            : (isWinner 
                ? '상대방을 물리쳤습니다!' 
                : '아쉽지만 다음 기회에...')}
        </p>
        <button className="game-board__action-btn" onClick={onExit}>
          홈으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="game-board">
      <div className="game-board__header">
        <button className="game-board__exit-btn" onClick={onExit}>
          ✕ 나가기
        </button>
        <div className="game-board__turn-info">
          <div className="game-board__turn-badge">
            <span className="game-board__turn-label">턴</span>
            <span className="game-board__turn-number">{gameState.turn}</span>
          </div>
          <div className={`game-board__turn-status ${isMyTurn ? 'game-board__turn-status--my' : ''}`}>
            {isMyTurn ? '⚔️ 내 턴' : '⏳ 상대 턴'}
          </div>
          {isMyTurn && timeRemaining !== null && (
            <div className={`game-board__timer ${timeRemaining <= 10 ? 'game-board__timer--warning' : ''}`}>
              ⏱️ {Math.ceil(timeRemaining)}초
            </div>
          )}
        </div>
      </div>

      {/* 상대 필드 */}
      <div className="game-board__opponent-field">
        <div className="game-board__player-panel game-board__player-panel--opponent">
          <div className="game-board__player-header">
            <div className="game-board__player-name">
              {opponentProfile?.avatar && <span className="game-board__player-avatar">{opponentProfile.avatar}</span>}
              <span>{opponentProfile?.username || '상대'}</span>
            </div>
            {!isMyTurn && timeRemaining !== null && (
              <div className={`game-board__timer ${timeRemaining <= 10 ? 'game-board__timer--warning' : ''}`}>
                ⏳ {Math.ceil(timeRemaining)}초
              </div>
            )}
          </div>
          <div className="game-board__player-stats">
            <div className="game-board__stat-badge game-board__stat-badge--life">
              <div className="game-board__stat-icon">❤️</div>
              <div className="game-board__stat-value">{opponentPlayer.life}</div>
            </div>
            {opponentPlayer.max_mana > 0 && (
              <div className="game-board__stat-badge game-board__stat-badge--mana">
                <div className="game-board__stat-icon">💎</div>
                <div className="game-board__stat-value">
                  <span className="game-board__mana-current">{opponentPlayer.mana}</span>
                  <span className="game-board__mana-separator">/</span>
                  <span className="game-board__mana-max">{opponentPlayer.max_mana}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="game-board__field">
          {opponentFieldCards && opponentFieldCards.length > 0 ? (
            opponentFieldCards.map((card) => {
              const fieldCard = opponentFieldCardMap.get(card.id);
              const isAttackTarget = attackMode !== null;
              
              return (
                <div
                  key={card.id}
                  className={`game-board__field-card ${isAttackTarget ? 'game-board__field-card--attack-target' : ''}`}
                >
                  <Card
                    card={card}
                    showBack={false}
                    onClick={() => {
                      if (isAttackTarget && attackMode) {
                        // 공격 실행
                        handleAttack(attackMode.attackerCardId, attackMode.attackerCard, card.id);
                        setAttackMode(null);
                      } else {
                        setSelectedFieldCard({ cardId: card.id, isMyField: false });
                      }
                    }}
                  />
                  {fieldCard && (
                    <div className="game-board__field-card-stats">
                      {fieldCard.hp !== undefined && (
                        <div className="game-board__field-card-hp">❤️ {fieldCard.hp}</div>
                      )}
                    </div>
                  )}
                  {isAttackTarget && (
                    <div className="game-board__field-card-target">공격 대상</div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="game-board__field-empty">
              필드가 비어있습니다
              {attackMode && (
                <div className="game-board__direct-attack-hint">
                  <button
                    className="game-board__direct-attack-btn"
                    onClick={() => {
                      if (attackMode) {
                        handleAttack(attackMode.attackerCardId, attackMode.attackerCard);
                        setAttackMode(null);
                      }
                    }}
                  >
                    직접 공격
                  </button>
                </div>
              )}
            </div>
          )}
          {attackMode && opponentFieldCards && opponentFieldCards.length === 0 && (
            <div className="game-board__direct-attack-hint">
              <button
                className="game-board__direct-attack-btn"
                onClick={() => {
                  if (attackMode) {
                    handleAttack(attackMode.attackerCardId, attackMode.attackerCard);
                    setAttackMode(null);
                  }
                }}
              >
                직접 공격
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 내 필드 */}
      <div className="game-board__my-field">
        <div className="game-board__field">
          {myFieldCards && myFieldCards.length > 0 ? (
            myFieldCards.map((card) => {
              const fieldCard = myFieldCardMap.get(card.id);
              const canAttack = isMyTurn && fieldCard && (!fieldCard.attack_count || fieldCard.attack_count === 0);
              
              return (
                <div key={card.id} className="game-board__field-card">
                  <Card
                    card={card}
                    showBack={false}
                    onClick={() => {
                      if (attackMode && attackMode.attackerCardId === card.id) {
                        // 공격 모드 취소
                        setAttackMode(null);
                      } else if (canAttack && !attackMode) {
                        // 공격 모드 시작
                        setAttackMode({ attackerCardId: card.id, attackerCard: card });
                      } else if (!canAttack) {
                        setSelectedFieldCard({ cardId: card.id, isMyField: true });
                      }
                    }}
                  />
                  {fieldCard && (
                    <div className="game-board__field-card-stats">
                      {fieldCard.hp !== undefined && (
                        <div className="game-board__field-card-hp">❤️ {fieldCard.hp}</div>
                      )}
                      {fieldCard.attack_count !== undefined && fieldCard.attack_count > 0 && (
                        <div className="game-board__field-card-attacked">⚔️ 공격함</div>
                      )}
                      {canAttack && !attackMode && (
                        <div className="game-board__field-card-attackable">⚔️ 공격 가능</div>
                      )}
                      {attackMode && attackMode.attackerCardId === card.id && (
                        <div className="game-board__field-card-selected">선택됨</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="game-board__field-empty">필드가 비어있습니다</div>
          )}
        </div>
        <div className="game-board__player-panel game-board__player-panel--my">
          <div className="game-board__player-header">
            <div className="game-board__player-name">
              <span>나</span>
            </div>
            {isMyTurn && timeRemaining !== null && (
              <div className={`game-board__timer ${timeRemaining <= 10 ? 'game-board__timer--warning' : ''}`}>
                ⏱️ {Math.ceil(timeRemaining)}초
              </div>
            )}
          </div>
          <div className="game-board__player-stats">
            <div className="game-board__stat-badge game-board__stat-badge--life">
              <div className="game-board__stat-icon">❤️</div>
              <div className="game-board__stat-value">{myPlayer.life}</div>
            </div>
            <div className="game-board__stat-badge game-board__stat-badge--mana">
              <div className="game-board__stat-icon">💎</div>
              <div className="game-board__stat-value">
                <span className="game-board__mana-current">{myPlayer.mana}</span>
                <span className="game-board__mana-separator">/</span>
                <span className="game-board__mana-max">{myPlayer.max_mana}</span>
              </div>
            </div>
            <div className="game-board__stat-badge game-board__stat-badge--deck">
              <div className="game-board__stat-icon">📚</div>
              <div className="game-board__stat-value">{myPlayer.deck?.length || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 핸드 */}
      <div className="game-board__hand">
        {handCards && handCards.length > 0 ? (
          handCards.map((card) => (
            <div key={card.id} className="game-board__hand-card">
              <Card
                card={card}
                showBack={false}
                onClick={() => setSelectedCardId(card.id)}
              />
            </div>
          ))
        ) : (
          <div className="game-board__hand-empty">핸드가 비어있습니다</div>
        )}
      </div>

      {/* 액션 버튼 */}
      {isMyTurn && (
        <div className="game-board__actions">
          <button
            className="game-board__action-btn game-board__action-btn--secondary"
            onClick={handleDrawCard}
            disabled={gameAction.isPending || (myPlayer.deck?.length || 0) === 0}
            title="카드 드로우 (덱에 카드가 있을 때만)"
          >
            카드 드로우
          </button>
          <button
            className="game-board__action-btn"
            onClick={handleEndTurn}
            disabled={gameAction.isPending}
          >
            턴 종료
          </button>
        </div>
      )}

      {/* 카드 상세 모달 (핸드) */}
      {selectedCardId && handCards && (
        <div className="game-board__card-modal-wrapper">
          <CardDetailModal
            card={handCards.find((c) => c.id === selectedCardId)!}
            isOpen={!!selectedCardId}
            onClose={() => setSelectedCardId(null)}
          />
          {isMyTurn && (
            <div className="game-board__card-actions">
              <button
                className="game-board__card-action-btn game-board__card-action-btn--play"
                onClick={() => {
                  const card = handCards.find((c) => c.id === selectedCardId);
                  if (card) {
                    handlePlayCard(selectedCardId, card);
                  }
                }}
                disabled={
                  gameAction.isPending ||
                  (myPlayer.mana < (handCards.find((c) => c.id === selectedCardId)?.cost || 0)) ||
                  (handCards.find((c) => c.id === selectedCardId)?.card_type === 'creature' &&
                    (myPlayer.field?.length || 0) >= 5)
                }
              >
                {handCards.find((c) => c.id === selectedCardId)?.card_type === 'creature'
                  ? '소환'
                  : handCards.find((c) => c.id === selectedCardId)?.card_type === 'spell'
                  ? '사용'
                  : '배치'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* 필드 카드 상세 모달 */}
      {selectedFieldCard && (
        <div className="game-board__card-modal-wrapper">
          <CardDetailModal
            card={
              (selectedFieldCard.isMyField ? myFieldCards : opponentFieldCards)?.find(
                (c) => c.id === selectedFieldCard.cardId
              )!
            }
            isOpen={!!selectedFieldCard}
            onClose={() => setSelectedFieldCard(null)}
          />
          {isMyTurn && selectedFieldCard.isMyField && (
            <div className="game-board__card-actions">
              {(() => {
                const card = myFieldCards?.find((c) => c.id === selectedFieldCard.cardId);
                const hasSkill = card?.skill_name && card?.skill_effect;
                if (!hasSkill) return null;

                return (
                  <button
                    className="game-board__card-action-btn game-board__card-action-btn--skill"
                    onClick={() => {
                      if (card) {
                        handleUseSkill(selectedFieldCard.cardId, card);
                      }
                    }}
                    disabled={
                      gameAction.isPending ||
                      (myPlayer.mana < ((card.skill_effect as any)?.cost || 0))
                    }
                  >
                    스킬 사용: {card.skill_name}
                  </button>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* 데미지 숫자 애니메이션 */}
      {damageNumbers.map((damage) => (
        <div
          key={damage.id}
          className="game-board__damage-number"
          style={{
            left: `${damage.x}px`,
            top: `${damage.y}px`,
          }}
        >
          -{damage.value}
        </div>
      ))}
    </div>
  );
}

